import type { i18n as I18nInstance } from 'i18next'

const collectNamespaces = (i18nInstance: I18nInstance) => {
  const namespaces = new Set<string>()

  const { options, store, reportNamespaces } = i18nInstance

  const defaultNamespaces = options?.ns
  if (typeof defaultNamespaces === 'string') {
    namespaces.add(defaultNamespaces)
  } else if (Array.isArray(defaultNamespaces)) {
    defaultNamespaces.filter(Boolean).forEach(namespace => namespaces.add(namespace))
  }

  if (store?.data) {
    Object.values(store.data).forEach(localeStore => {
      if (!localeStore) return

      Object.keys(localeStore).forEach(namespace => namespaces.add(namespace))
    })
  }

  const usedNamespaces = reportNamespaces?.getUsedNamespaces?.()
  if (usedNamespaces) {
    usedNamespaces.filter(Boolean).forEach(namespace => namespaces.add(namespace))
  }

  return Array.from(namespaces).filter(Boolean)
}

const loadNamespaceModule = async (locale: string, namespace: string) => {
  try {
    const moduleTranslation = await import(
      /* webpackChunkName: "i18n-[request]" */
      /* webpackMode: "lazy" */
      `../../../public/locales/${locale}/${namespace}.json`
    )
    return moduleTranslation.default ?? moduleTranslation
  } catch (error) {
    const details = error instanceof Error ? error.message : 'Unknown error'
    throw new Error(`Failed to load locale namespace ${locale}/${namespace}: ${details}`)
  }
}

export const loadLocaleBundles = async (
  i18nInstance: I18nInstance | null | undefined,
  locale: string | undefined,
) => {
  if (!i18nInstance || !locale) return

  const namespaces = collectNamespaces(i18nInstance)
  if (namespaces.length === 0) return

  for (const namespace of namespaces) {
    if (!namespace) continue
    if (i18nInstance.hasResourceBundle(locale, namespace)) continue

    const resources = await loadNamespaceModule(locale, namespace)
    i18nInstance.addResourceBundle(locale, namespace, resources, true, true)
  }
}
